import { useState, useEffect, useCallback, useMemo } from "react";
import { useResourceSkillApi } from "../../hooks/useResourceSkillApi";
import { useResourceApi } from "../../hooks/useResourceApi";
import { useSkillApi } from "../../hooks/useSkillApi";
import { useLoading } from "../../contexts/LoadingContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import StarIcon from "@mui/icons-material/Star";
import CircleIcon from "@mui/icons-material/Circle";
import { Typography, Chip } from "@mui/material";
import { useRbac } from "../../hooks/RbacContext";

export const useAllResourceSkillsViewModel = () => {
  const [isAddSkillOpen, setIsAddSkillOpen] = useState(false);
  const [selectedSkillMapping, setSelectedSkillMapping] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { checkAccess } = useRbac();

  const { showLoading, hideLoading } = useLoading();

  const {
    getResourceSkillMappings,
    deleteResourceSkillMapping,
    createResourceSkillMapping,
    updateResourceSkillMapping,
    cacheOperations: { readResourceSkillMappings, writeResourceSkillMappings },
  } = useResourceSkillApi();

  const {
    getResources,
    cacheOperations: { readResources, writeResources },
  } = useResourceApi();

  const {
    getSkills,
    cacheOperations: { readSkills, writeSkills },
  } = useSkillApi();

  const handleAddSkill = useCallback(() => {
    setSelectedSkillMapping(null);
    setIsAddSkillOpen(true);
  }, []);

  const handleEditSkill = useCallback((skillMapping) => {
    setSelectedSkillMapping(skillMapping);
    setIsAddSkillOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsAddSkillOpen(false);
    setSelectedSkillMapping(null);
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        showLoading();
        let result;

        if (selectedSkillMapping) {
          result = await updateResourceSkillMapping(
            selectedSkillMapping.resource_skill_mapping_id,
            formData
          );
        } else {
          result = await createResourceSkillMapping(formData);
        }

        if (!result.success) {
          setSuccessModal({
            type: "error",
            title: "Action Failed",
            message: result.error,
          });
          return { success: false, error: result.error };
        }

        setSuccessModal({
          type: "success",
          title: "Success",
          message: selectedSkillMapping
            ? "Resource skill mapping updated successfully"
            : "Resource skill mapping added successfully",
        });
        handleCloseDialog();
        return { success: true };
      } catch (error) {
        setSuccessModal({
          type: "error",
          title: "Action Failed",
          message: error.message || "An error occurred",
        });
        return { success: false, error: error.message };
      } finally {
        hideLoading();
      }
    },
    [
      selectedSkillMapping,
      updateResourceSkillMapping,
      createResourceSkillMapping,
      showLoading,
      hideLoading,
      handleCloseDialog,
    ]
  );

  const handleDeleteClick = useCallback((mapping) => {
    setDeleteConfirmation({
      mapping,
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmation) return;

    try {
      showLoading();
      const result = await deleteResourceSkillMapping(
        deleteConfirmation.mapping.resource_skill_mapping_id
      );

      if (!result.success) {
        throw new Error(result.error);
      }

      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Resource skill mapping deleted successfully",
      });
      setDeleteConfirmation(null);
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message,
      });
    } finally {
      hideLoading();
    }
  }, [
    deleteConfirmation,
    deleteResourceSkillMapping,
    showLoading,
    hideLoading,
  ]);

  const fetchData = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    try {
      const [mappingsResult, resourcesResult, skillsResult] = await Promise.all(
        [getResourceSkillMappings(), getResources(), getSkills()]
      );

      if (
        !mappingsResult.success ||
        !resourcesResult.success ||
        !skillsResult.success
      ) {
        throw new Error("Failed to fetch required data");
      }

      // Write to cache only if we have data
      if (mappingsResult.data?.GetResourceSkillMappings) {
        writeResourceSkillMappings(
          mappingsResult.data.GetResourceSkillMappings
        );
      }
      if (resourcesResult.data?.GetResources) {
        writeResources(resourcesResult.data.GetResources);
      }
      if (skillsResult.data?.GetSkills) {
        writeSkills(skillsResult.data.GetSkills);
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    getResourceSkillMappings,
    getResources,
    getSkills,
    writeResourceSkillMappings,
    writeResources,
    writeSkills,
    loading,
  ]);

  useEffect(() => {
    fetchData();
  }, []);

  const getResourceName = useMemo(
    () => (id) => {
      if (!id) return "N/A";
      const resources = readResources();
      const resource = resources.find(
        (r) => Number(r.resource_id) === Number(id)
      );
      return resource ? `${resource.first_name} ${resource.last_name}` : "N/A";
    },
    [readResources]
  );

  const getSkillName = useMemo(
    () => (id) => {
      if (!id) return "N/A";
      const skills = readSkills();
      const skill = skills.find((s) => Number(s.skill_id) === Number(id));
      return skill?.skill_name || `Skill ${id}`;
    },
    [readSkills]
  );

  const getActions = useCallback(
    (mapping) => {
      const actions = [];
      if (checkAccess("resourceSkillMapping", "update")) {
        actions.push({
          icon: <EditIcon fontSize="small" />,
          label: "Edit",
          onClick: () => handleEditSkill(mapping),
          color: "primary",
          sx: {
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
            },
          },
        });
      }
      if (checkAccess("resourceSkillMapping", "delete")) {
        actions.push({
          icon: <DeleteIcon fontSize="small" />,
          label: "Delete",
          onClick: () => handleDeleteClick(mapping),
          color: "error",
          sx: {
            "&:hover": {
              backgroundColor: (theme) =>
                theme.palette.error.lighter || "rgba(244, 67, 54, 0.08)",
            },
          },
        });
      }

      return actions;

    },
    [handleEditSkill, handleDeleteClick]
  );

  const columns = useMemo(
    () => [
      {
        id: "resource_id",
        label: "Resource",
        render: (value) => (
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: "inherit",
              color: (theme) =>
                value
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
            }}
          >
            {getResourceName(value)}
          </Typography>
        ),
        searchValue: (value) => getResourceName(value),
      },
      {
        id: "skill_id",
        label: "Skill",
        render: (value) => (
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: "inherit",
              color: (theme) =>
                value
                  ? theme.palette.text.primary
                  : theme.palette.text.secondary,
            }}
          >
            {getSkillName(value)}
          </Typography>
        ),
        searchValue: (value) => getSkillName(value),
      },
      {
        id: "year_of_experience",
        label: "Experience (Years)",
        align: "left",
      },
      {
        id: "proficient_level_enum",
        label: "Proficiency Level",
        align: "left",
        render: (value) => value?.replace("_", " ").toLowerCase(),
        searchValue: (value) => value?.replace("_", " ").toLowerCase(),
      },
      {
        id: "created_on",
        label: "Created On",
        align: "left",
      },
      {
        id: "updated_on",
        label: "Updated On",
        align: "left",
      },
      {
        id: "is_active",
        label: "Status",
        align: "left",
        render: (value) => (
          <Chip
            label={value ? "Active" : "Inactive"}
            size="small"
            sx={{
              height: 24,
              fontSize: "0.75rem",
              fontWeight: 500,
              backgroundColor: value ? "#E8F5E9" : "#FFF3E0",
              color: value ? "#2E7D32" : "#EF6C00",
              "& .MuiChip-label": {
                px: 1.5,
              },
            }}
          />
        ),
        searchValue: (value) => (value ? "active" : "inactive"),
      },
    ],
    [getResourceName, getSkillName]
  );

  const fixedColumns = useMemo(
    () => ({
      left: [],
      right: ["is_active", "actions"],
    }),
    []
  );

  const columnFilters = useMemo(
    () => [
      {
        id: "resource_id",
        type: "select",
        label: "Resource",
        render: (value) => getResourceName(value),
        icon: <PersonIcon />,
        sendIcon: true,
        options: readResources()
          .filter((r) => r.is_active)
          .map((r) => ({
            value: r.resource_id,
            label: `${r.first_name} ${r.last_name}`,
          })),
      },
      {
        id: "skill_id",
        type: "select",
        label: "Skill",
        render: (value) => getSkillName(value),
        icon: <WorkIcon />,
        sendIcon: true,
        options: readSkills()
          .filter((s) => s.is_active)
          .map((s) => ({
            value: s.skill_id,
            label: s.skill_name,
          })),
      },
      {
        id: "proficient_level_enum",
        type: "select",
        label: "Proficiency",
        icon: <StarIcon />,
        sendIcon: true,
        options: [
          { value: "LEVEL_1", label: "Level 1" },
          { value: "LEVEL_2", label: "Level 2" },
          { value: "LEVEL_3", label: "Level 3" },
          { value: "PROFESSIONAL_EXPERT", label: "Professional Expert" },
        ],
      },
      {
        id: "is_active",
        type: "select",
        label: "Status",
        render: (value) => (value ? "Active" : "Inactive"),
        icon: <CircleIcon />,
        sendIcon: true,
        options: [
          { value: "true", label: "Active" },
          { value: "false", label: "Inactive" },
        ],
      },
    ],
    [getResourceName, getSkillName, readResources, readSkills]
  );

  return {
    isAddSkillOpen,
    selectedSkillMapping,
    successModal,
    deleteConfirmation,
    loading,
    error,
    columns,
    columnFilters,
    fixedColumns,
    resourceSkillMappings: readResourceSkillMappings()
      .filter((m) => m.is_active)
      .map((m) => ({
        ...m,
        resource_id: Number(m.resource_id),
        skill_id: Number(m.skill_id),
      })),
    resources: readResources()
      .filter((r) => r.is_active)
      .map((r) => ({
        ...r,
        resource_id: Number(r.resource_id),
      })),
    skills: readSkills()
      .filter((s) => s.is_active)
      .map((s) => ({
        ...s,
        skill_id: Number(s.skill_id),
        skill_name: s.skill_name || `Skill ${s.skill_id}`,
      })),
    handleAddSkill,
    handleEditSkill,
    handleCloseDialog,
    handleSubmit,
    setSuccessModal,
    setDeleteConfirmation,
    handleDeleteClick,
    handleDeleteConfirm,
    getResourceName,
    getSkillName,
    getActions,
    refetch: fetchData,
  };
};
